/**
 * Jurisdiction Directory - Admin Portal
 * Phase 2: Admin UI for Jurisdiction Management
 * 
 * Features:
 * - List all jurisdictions with pagination
 * - Search by name or type
 * - Create new jurisdiction (via stepper wizard)
 * - Edit existing jurisdiction (via stepper wizard)
 * - Soft delete (deactivate) jurisdiction
 * - Admin-only access
 * - Global Column Sorting via useTableSort hook
 */
import React, { useState, useEffect, useMemo } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useDebouncedSearch } from '../hooks/useDebouncedSearch';
import { getUserFromToken } from '../utils/adminApi';
import ConfirmDialog from '../components/common/ConfirmDialog';
import { toast } from '../utils/toast';
import { formatUSPhone, validateAndFormatPhone, stripNonNumeric } from '../utils/phoneFormatter';
import {
  pickCheckPayableName,
  pickPayeePhone,
  pickPayeeMailingAddress,
} from '../utils/effectivePayee';
import useTableSort, { sortData } from '../hooks/useTableSort';
import {
  JURISDICTION_TYPE_OPTIONS,
  isCanonicalJurisdictionType,
  getJurisdictionTypeLabel,
  getJurisdictionTypeLabelForState,
} from '../lib/jurisdictionTaxonomy';
import JurisdictionWizardModal from '../components/jurisdiction/JurisdictionWizardModal';
import CSVUploadModal from '../components/jurisdiction/CSVUploadModal';
import JurisdictionAuditDrawer from '../components/jurisdiction/JurisdictionAuditDrawer';
import { Button } from '../components/ui/button';
import { Plus, Download, Upload, Search, FileSpreadsheet, Pencil, ChevronLeft, ChevronRight, ChevronsLeft, ChevronsRight, X, History, Trash2, AlertTriangle, Landmark } from 'lucide-react';
import './JurisdictionDirectory.css';
import { formatDateTimeET } from '../utils/formatters';

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL;

const JurisdictionDirectory = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const [jurisdictions, setJurisdictions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [pagination, setPagination] = useState({
    page: 1,
    pageSize: 25,
    totalPages: 0,
    total: 0
  });
  // Honour `?search=` from the Dashboard Quick Search (JURISDICTION-HEALTH-CLEANUP-001 rebalance).
  const { searchInput: searchInputDisplay, setSearchInput: setSearchInputDisplay, debouncedValue: searchQuery, clearSearch: clearSearchState } = useDebouncedSearch(searchParams.get('search') || '', 350);
  const [typeFilter, setTypeFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('active'); // 'active', 'inactive', 'all'
  // Jun 2026 — directory summary row (Total / Active / Inactive KPI badges).
  // Populated from the list endpoint's `summary` field; counts ignore the
  // active/inactive filter so the badges describe the full universe.
  const [summary, setSummary] = useState(null);
  const [formDialogOpen, setFormDialogOpen] = useState(false);
  const [editingJurisdiction, setEditingJurisdiction] = useState(null);
  // ?focus=<id>&tab=assignments URL-driven auto-open. Set by the
  // CollectorGuidanceBanner "Create Assignment" inline action so processors
  // can land directly on the right jurisdiction + tab without manual hunting.
  const [pendingFocusTab, setPendingFocusTab] = useState(null);
  const [actionLoading, setActionLoading] = useState(null);
  const [uploadModalOpen, setUploadModalOpen] = useState(false);

  // Activity Timeline drawer (Feb 22, 2026 — Session 19)
  // Only admin + jurisdiction_manager roles are permitted to open it; backend
  // enforces this independently, the UI hides the trigger for other roles.
  const [auditDrawer, setAuditDrawer] = useState({ isOpen: false, jurisdiction: null });
  
  // Confirmation dialog
  const [confirmDialog, setConfirmDialog] = useState({
    isOpen: false,
    title: '',
    message: '',
    onConfirm: null
  });

  // Feb 27, 2026 — Admin-only delete confirmation with dependency pre-check.
  // `target` is the jurisdiction being deleted; `deps` holds the results
  // of GET /dependencies so the modal can warn with real counts.
  const [deleteModal, setDeleteModal] = useState({
    isOpen: false,
    target: null,
    deps: null,
    depsLoading: false,
    submitting: false,
  });

  const currentUser = getUserFromToken();
  const token = localStorage.getItem('token');

  // Table sorting hook
  const {
    sortConfig,
    handleSort,
    getSortDirection
  } = useTableSort({
    defaultSortKey: 'created_at',
    defaultSortOrder: 'desc'
  });

  // Sort jurisdictions based on current sort configuration.
  // Jul 2026 (JUR-DIR-COUNTY-001): rows are enriched with `display_county`
  // (parent county for sub-jurisdictions; own name for county-level rows)
  // so requesters can spot same-name jurisdictions in different counties
  // before submitting a duplicate request.
  const getDisplayCounty = (j) => {
    if (j.parent_county_name) return j.parent_county_name;
    const t = (j.jurisdiction_type || '').toLowerCase();
    if (t === 'county' || t === 'parish') return j.jurisdiction_name || '';
    return '';
  };
  const sortedJurisdictions = useMemo(() => {
    if (!jurisdictions.length) return jurisdictions;
    // PAYEE-CHECKNAME-001 (Jul 2026): display_check_payable resolves the
    // assignment collector's Make Checks Payable To (never its display
    // name) so the column shows the real payee and sorts on it.
    const enriched = jurisdictions.map(j => ({
      ...j,
      display_county: getDisplayCounty(j),
      display_check_payable: pickCheckPayableName(j),
    }));
    // Use sortConfig.key and sortConfig.direction (not sortKey/sortOrder)
    return sortData(enriched, sortConfig.key, sortConfig.direction);
  }, [jurisdictions, sortConfig]);

  // AbortController ref to cancel stale fetch requests
  const abortControllerRef = React.useRef(null);
  // JURISDICTION-DELETE-UI-001: ids deleted from this screen — a list served by a replica whose cache has not
  // caught up yet (≤ 2 s) must never re-render them; we hide them and re-sync shortly after.
  const deletedIdsRef = React.useRef(new Set());
  const fetchRef = React.useRef(null);

  // Fetch jurisdictions
  const fetchJurisdictions = async () => {
    // Cancel any in-flight request to prevent stale data from overwriting fresh results
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    const controller = new AbortController();
    abortControllerRef.current = controller;

    console.log('Fetching jurisdictions with filter:', statusFilter);
    setLoading(true);
    setError(null);
    
    try {
      const params = new URLSearchParams({
        page: pagination.page.toString(),
        page_size: pagination.pageSize.toString()
      });
      
      // Status filter
      if (statusFilter === 'active') {
        params.append('active_only', 'true');
      } else if (statusFilter === 'inactive') {
        params.append('active_only', 'false');
      }
      // If 'all', don't add active_only param
      
      if (searchQuery) {
        params.append('search', searchQuery);
        // P0 fix (Feb 21, 2026): the backend default now does strict
        // name-family matching only (jurisdiction_name + canonical_name +
        // tax_collector_name) to prevent address-overlap contamination in
        // programmatic matchers. This admin directory UI wants the legacy
        // cross-field discovery (name + addresses + phone), so explicitly
        // opt in via search_field=all.
        params.append('search_field', 'all');
      }
      
      if (typeFilter) {
        params.append('jurisdiction_type', typeFilter);
      }

      console.log('Fetching:', `${API_BASE_URL}/api/jurisdictions?${params}`);

      const response = await fetch(`${API_BASE_URL}/api/jurisdictions?${params}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        signal: controller.signal
      });

      if (!response.ok) {
        let detail = '';
        try {
          const errBody = await response.json();
          detail = errBody.detail || errBody.message || JSON.stringify(errBody);
        } catch {
          detail = response.statusText || 'Unknown error';
        }
        throw new Error(`HTTP ${response.status}: ${detail}`);
      }

      const data = await response.json();
      console.log('Fetched jurisdictions:', data.jurisdictions?.length, 'Total:', data.total);
      const rows = data.jurisdictions || [];
      const ghosts = rows.filter(j => deletedIdsRef.current.has(j.id));
      if (ghosts.length) {
        console.log('Stale list from a lagging replica — hiding', ghosts.length, 'deleted row(s), re-syncing');
        setTimeout(() => fetchRef.current && fetchRef.current(), 2500);
      }
      const summary = data.summary
        ? {
            ...data.summary,
            total: Math.max(0, (data.summary.total || 0) - ghosts.length),
            active: Math.max(0, (data.summary.active || 0) - ghosts.filter(j => j.is_active).length),
            inactive: Math.max(0, (data.summary.inactive || 0) - ghosts.filter(j => !j.is_active).length),
          }
        : null;
      setJurisdictions(rows.filter(j => !deletedIdsRef.current.has(j.id)));
      setSummary(summary);
      setPagination(prev => ({
        ...prev,
        total: Math.max(0, (data.total || 0) - ghosts.length),
        totalPages: data.total_pages
      }));
    } catch (err) {
      // Ignore abort errors — they're expected when a newer request supersedes an older one
      if (err.name === 'AbortError') {
        console.log('Fetch aborted (superseded by newer request)');
        return;
      }
      console.error('Error fetching jurisdictions:', err);
      const friendlyMsg = 'Something went wrong loading jurisdictions. Please try again.';
      setError(friendlyMsg);
      // Reset count so it doesn't show stale total alongside error
      setPagination(prev => ({ ...prev, total: 0, totalPages: 0 }));
      setSummary(null);
      setJurisdictions([]);
      toast.error(friendlyMsg);
    } finally {
      setLoading(false);
    }
  };
  fetchRef.current = fetchJurisdictions;


  useEffect(() => {
    fetchJurisdictions();
  }, [pagination.page, searchQuery, typeFilter, statusFilter]);

  // ── URL-driven auto-open (?focus=<id>&tab=assignments) ──
  // When the CollectorGuidanceBanner's "Create Assignment" inline action
  // navigates here, we land with ?focus set. As soon as the requested
  // jurisdiction appears in the loaded list, we open its edit modal with
  // the requested tab pre-selected.
  //
  // Edge case: the focused jurisdiction may not be in the current page of
  // results (filtered out). We fall back to fetching it directly by ID.
  useEffect(() => {
    const focusId = searchParams.get('focus');
    const tab = searchParams.get('tab') || 'details';
    if (!focusId) return;
    // Try to find it in the already-loaded list first
    const hit = jurisdictions.find((j) => j.id === focusId);
    if (hit) {
      setEditingJurisdiction(hit);
      setPendingFocusTab((tab === 'assignments' || tab === 'research' || tab === 'mls-services') ? tab : 'details');
      setFormDialogOpen(true);
      // Clear the URL params so a refresh doesn't keep re-opening the modal.
      const next = new URLSearchParams(searchParams);
      next.delete('focus');
      next.delete('tab');
      setSearchParams(next, { replace: true });
      return;
    }
    // Not in the current filtered list — fetch by ID directly.
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${API_BASE_URL}/api/jurisdictions/${focusId}`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok) return;
        const j = await res.json();
        if (!cancelled && j?.id) {
          setEditingJurisdiction(j);
          setPendingFocusTab((tab === 'assignments' || tab === 'research' || tab === 'mls-services') ? tab : 'details');
          setFormDialogOpen(true);
          const next = new URLSearchParams(searchParams);
          next.delete('focus');
          next.delete('tab');
          setSearchParams(next, { replace: true });
        }
      } catch (_) { /* silent — URL nudge, not a hard requirement */ }
    })();
    return () => { cancelled = true; };
  }, [searchParams, jurisdictions, token, setSearchParams]);

  // Handle search — only reset to page 1; the useEffect handles the actual fetch
  const handleSearch = (e) => {
    e.preventDefault();
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  // Handle create
  const handleCreate = () => {
    setEditingJurisdiction(null);
    setFormDialogOpen(true);
  };

  // Handle edit
  const handleEdit = (jurisdiction) => {
    setEditingJurisdiction(jurisdiction);
    setFormDialogOpen(true);
  };

  // ── Feb 27, 2026 (revised) — Admin-only HARD delete with reference safeguard ──
  //
  // Open the confirmation modal and kick off a dependency count fetch.
  // - If has_dependencies → modal blocks the action, shows "cannot be
  //   permanently deleted" message, only Cancel button.
  // - If safe (zero refs) → modal warns this is permanent + irreversible,
  //   user confirms → DELETE → row physically removed from DB.
  //
  // Active/Inactive toggle is a separate concern handled in the Edit dialog
  // (`formData.is_active`) and continues to soft-toggle without removing rows.
  const openDeleteConfirm = async (jurisdiction) => {
    setDeleteModal({
      isOpen: true,
      target: jurisdiction,
      deps: null,
      depsLoading: true,
      submitting: false,
    });
    try {
      const res = await fetch(
        `${API_BASE_URL}/api/jurisdictions/${jurisdiction.id}/dependencies`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (res.status === 404) {
        // JURISDICTION-DELETE-UI-001: record is gone (deleted elsewhere) — never leave a stale row/modal behind
        toast.error(`${jurisdiction.jurisdiction_name} no longer exists — refreshing the directory`);
        setDeleteModal({ isOpen: false, target: null, deps: null, depsLoading: false, submitting: false });
        fetchJurisdictions();
        return;
      }
      const data = res.ok ? await res.json() : null;
      setDeleteModal((prev) =>
        prev.target?.id === jurisdiction.id
          ? { ...prev, deps: data, depsLoading: false }
          : prev
      );
    } catch {
      setDeleteModal((prev) =>
        prev.target?.id === jurisdiction.id
          ? { ...prev, deps: null, depsLoading: false }
          : prev
      );
    }
  };

  const handleConfirmDelete = async () => {
    const target = deleteModal.target;
    if (!target) return;
    setDeleteModal((prev) => ({ ...prev, submitting: true }));
    setActionLoading(target.id);
    try {
      const res = await fetch(`${API_BASE_URL}/api/jurisdictions/${target.id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      });
      if (res.status === 404) {
        // JURISDICTION-DELETE-UI-001: already gone — close, refresh, no stale row
        toast.error(`${target.jurisdiction_name} no longer exists — refreshing the directory`);
        setDeleteModal({ isOpen: false, target: null, deps: null, depsLoading: false, submitting: false });
        fetchJurisdictions();
        return;
      }
      if (!res.ok) {
        // 409 = blocked by references; surface the server message.
        const err = await res.json().catch(() => ({}));
        const msg =
          (err && err.detail && (err.detail.message || err.detail)) ||
          'Delete failed';
        throw new Error(typeof msg === 'string' ? msg : 'Delete failed');
      }
      toast.success(`Permanently deleted ${target.jurisdiction_name}`);
      deletedIdsRef.current.add(target.id);
      setJurisdictions((prev) => prev.filter((j) => j.id !== target.id));
      setDeleteModal({ isOpen: false, target: null, deps: null, depsLoading: false, submitting: false });
      fetchJurisdictions();
    } catch (e) {
      toast.error(e.message || 'Failed to delete jurisdiction');
      setDeleteModal((prev) => ({ ...prev, submitting: false }));
    } finally {
      setActionLoading(null);
    }
  };

  // Handle CSV download
  const handleDownloadCSV = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/jurisdictions/export/csv?active_only=true`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        throw new Error('Failed to download CSV');
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `jurisdictions_export_${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      
      toast.success('CSV exported successfully');
    } catch (err) {
      console.error('Error downloading CSV:', err);
      toast.error('Failed to download CSV');
    }
  };

  // Handle Excel download
  const handleDownloadExcel = async () => {
    try {
      const response = await fetch(`${API_BASE_URL}/api/jurisdictions/export/excel?active_only=true`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });

      if (!response.ok) {
        throw new Error('Failed to download Excel');
      }

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `jurisdictions_export_${new Date().toISOString().split('T')[0]}.xlsx`;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      
      toast.success('Excel exported successfully');
    } catch (err) {
      console.error('Error downloading Excel:', err);
      toast.error('Failed to download Excel');
    }
  };

  // Handle upload modal
  const handleUploadCSV = () => {
    setUploadModalOpen(true);
  };

  // Pagination handlers
  const handlePageChange = (newPage) => {
    const clamped = Math.max(1, Math.min(newPage, pagination.totalPages || 1));
    if (clamped === pagination.page) return;
    setPagination(prev => ({ ...prev, page: clamped }));
  };

  // Build a compact list of page numbers with ellipses around the current page.
  // Examples (current of total):
  //   1 of 8    →  [1, 2, 3, '…', 8]
  //   4 of 8    →  [1, '…', 3, 4, 5, '…', 8]
  //   8 of 8    →  [1, '…', 6, 7, 8]
  //   2 of 3    →  [1, 2, 3]          (no ellipses when small)
  const buildPageList = (current, total) => {
    if (!total || total <= 1) return [1];
    const windowSize = 1;              // pages shown on each side of current
    const pages = new Set([1, total, current]);
    for (let i = 1; i <= windowSize; i++) {
      if (current - i > 1) pages.add(current - i);
      if (current + i < total) pages.add(current + i);
    }
    const sorted = Array.from(pages).sort((a, b) => a - b);
    const out = [];
    for (let i = 0; i < sorted.length; i++) {
      out.push(sorted[i]);
      if (i < sorted.length - 1 && sorted[i + 1] - sorted[i] > 1) {
        out.push('ellipsis-' + sorted[i]);
      }
    }
    return out;
  };

  // "Go to page" input — controlled value separate from current page so the
  // user can type freely; commits on Enter or blur/Go click.
  const [pageJumpInput, setPageJumpInput] = useState('');
  const commitPageJump = () => {
    const n = parseInt(pageJumpInput, 10);
    if (!Number.isNaN(n)) handlePageChange(n);
    setPageJumpInput('');
  };

  // Format date
  const formatDate = (dateString) => {
    if (!dateString) return '—';
    try {
      return formatDateTimeET(dateString);
    } catch {
      return '—';
    }
  };

  // Type badge color
  const getTypeBadgeColor = (type) => {
    const colors = {
      'county': 'badge-blue',
      'city': 'badge-green',
      'town': 'badge-teal',
      'township': 'badge-purple',
      'parish': 'badge-amber',
      'borough': 'badge-orange',
      'village': 'badge-orange',
      'school_district': 'badge-pink',
      'other': 'badge-gray'
    };
    return colors[type] || 'badge-gray';
  };

  // Format type display — state-aware (Jun 2026): legacy LA rows stored
  // as `county` display as "Parish" without any data change.
  const formatType = (type, state) => {
    if (!type) return '—';
    return getJurisdictionTypeLabelForState(type, state);
  };

  return (
    <div className="jurisdiction-directory jx-premium">
      {/* Header */}
      <div className="page-header">
        <div className="page-header-left">
          <span className="jx-head-icon" aria-hidden="true"><Landmark /></span>
          <div>
            <h1 className="page-title">Jurisdiction Directory</h1>
            <p className="page-subtitle">Manage tax authorities and collector information</p>
          </div>
        </div>
        <div className="page-header-right">
          {/* Role-gated actions — Session 24 (Feb 22, 2026):
              jurisdiction_requestor cannot Bulk Upload or directly Add;
              they only get a "Submit New Request" button that opens the
              wizard in request mode. Everyone else sees the existing
              buttons unchanged. */}
          {(currentUser?.role === 'admin' ||
            currentUser?.role === 'jurisdiction_manager' ||
            currentUser?.role === 'jurisdiction_requestor') && (
            <Button
              variant="outline"
              onClick={() => navigate('/admin/jurisdiction-requests')}
              data-testid="view-requests-btn"
            >
              <History className="w-4 h-4" />
              {currentUser?.role === 'jurisdiction_requestor' ? 'My Requests' : 'Requests Queue'}
            </Button>
          )}
          {currentUser?.role === 'admin' && (
            <>
              <Button variant="outline" onClick={handleDownloadCSV}>
                <Download className="w-4 h-4" />
                Download CSV
              </Button>
              <Button variant="outline" onClick={handleDownloadExcel}>
                <FileSpreadsheet className="w-4 h-4" />
                Download Excel
              </Button>
            </>
          )}
          {currentUser?.role !== 'jurisdiction_requestor' && (
            <Button variant="outline" onClick={handleUploadCSV}>
              <Upload className="w-4 h-4" />
              Bulk Upload
            </Button>
          )}
          {currentUser?.role === 'jurisdiction_requestor' ? (
            <Button onClick={handleCreate} data-testid="submit-request-btn">
              <Plus className="w-4 h-4" />
              Submit New Request
            </Button>
          ) : (
            <Button onClick={handleCreate} data-testid="add-jurisdiction-btn">
              <Plus className="w-4 h-4" />
              Add Jurisdiction
            </Button>
          )}
        </div>
      </div>

      {/* Filters */}
      <div className="filters-bar">
        <form onSubmit={handleSearch} className="search-form">
          <input
            type="text"
            placeholder="Search by name, check payable name, phone, or address..."
            value={searchInputDisplay}
            onChange={(e) => setSearchInputDisplay(e.target.value)}
            className="search-input"
            style={{ minWidth: '320px' }}
            data-testid="jurisdiction-search-input"
          />
          <Button type="submit">
            <Search className="w-4 h-4" />
            Search
          </Button>
        </form>

        <select
          value={typeFilter}
          onChange={(e) => {
            setTypeFilter(e.target.value);
            setPagination(prev => ({ ...prev, page: 1 }));
          }}
          className="filter-select"
        >
          <option value="">All Types</option>
          {/* Feb 2026 — sourced from canonical jurisdictionTaxonomy so
              the filter chip list cannot drift from the create/edit
              dropdowns. */}
          {JURISDICTION_TYPE_OPTIONS.map((opt) => (
            <option key={opt.value} value={opt.value}>{opt.label}</option>
          ))}
        </select>

        <select
          value={statusFilter}
          onChange={(e) => {
            setStatusFilter(e.target.value);
            setPagination(prev => ({ ...prev, page: 1 }));
          }}
          className="filter-select"
          data-testid="status-filter-select"
        >
          <option value="active">Active Only</option>
          <option value="all">All Jurisdictions</option>
          <option value="inactive">Inactive Only</option>
        </select>
      </div>

      {/* Stats — Jun 2026 redesign: "Showing N jurisdictions" beneath the
          search bar with the count emphasized, plus Total / Active /
          Inactive KPI badges (counts ignore the status filter). */}
      {!loading && !error && (
        <div className="stats-bar" data-testid="directory-stats-bar">
          <span className="stat-item" data-testid="directory-showing-count">
            Showing <strong>{(pagination.total || 0).toLocaleString()}</strong>{' '}
            {pagination.total === 1 ? 'jurisdiction' : 'jurisdictions'}
            {statusFilter !== 'all' && (
              <span className="stat-filter-hint">
                {' '}· {statusFilter === 'active' ? 'Active Only' : 'Inactive Only'}
              </span>
            )}
          </span>
          {summary && (
            <div className="stat-kpis">
              <span className="stat-kpi" data-testid="directory-kpi-total">
                Total <strong>{(summary.total || 0).toLocaleString()}</strong>
              </span>
              <span className="stat-kpi stat-kpi-active" data-testid="directory-kpi-active">
                Active <strong>{(summary.active || 0).toLocaleString()}</strong>
              </span>
              <span className="stat-kpi stat-kpi-inactive" data-testid="directory-kpi-inactive">
                Inactive <strong>{(summary.inactive || 0).toLocaleString()}</strong>
              </span>
            </div>
          )}
        </div>
      )}

      {/* Table */}
      {loading ? (
        <div className="loading-state">
          <div className="spinner"></div>
          <p>Loading jurisdictions...</p>
        </div>
      ) : error ? (
        <div className="error-state">
          <p className="error-message">{error}</p>
          <Button variant="outline" onClick={fetchJurisdictions}>Retry</Button>
        </div>
      ) : jurisdictions.length === 0 ? (
        <div className="empty-state">
          <div className="empty-state-icon">📋</div>
          <h3 className="empty-state-title">No jurisdictions found</h3>
          <p className="empty-state-description">
            {searchQuery || typeFilter || statusFilter !== 'active' 
              ? 'Try adjusting your filters to see more results.' 
              : 'Use the button above to add your first jurisdiction.'}
          </p>
        </div>
      ) : (
        <>
          <div className="table-container">
            <table className="jurisdictions-table sortable-table">
              {/* JD-ACTIONS-ALIGN-001: fixed column plan — content can never shift the Actions column */}
              <colgroup>
                <col className="col-status" />
                <col className="col-type" />
                <col className="col-state" />
                <col className="col-county" />
                <col className="col-name" />
                <col className="col-check-payable" />
                <col className="col-phone" />
                <col className="col-address" />
                <col className="col-date" />
                <col className="col-date" />
                <col className="col-actions" />
              </colgroup>
              <thead>
                <tr>
                  <th 
                    className={`sortable-header ${getSortDirection('is_active') ? 'sorted' : ''}`}
                    onClick={() => handleSort('is_active')}
                  >
                    <div className="th-content">
                      <span>Status</span>
                      {getSortDirection('is_active') && (
                        <span className="sort-indicator">{getSortDirection('is_active') === 'asc' ? '↑' : '↓'}</span>
                      )}
                    </div>
                  </th>
                  <th 
                    className={`sortable-header ${getSortDirection('jurisdiction_type') ? 'sorted' : ''}`}
                    onClick={() => handleSort('jurisdiction_type')}
                  >
                    <div className="th-content">
                      <span>Type</span>
                      {getSortDirection('jurisdiction_type') && (
                        <span className="sort-indicator">{getSortDirection('jurisdiction_type') === 'asc' ? '↑' : '↓'}</span>
                      )}
                    </div>
                  </th>
                  <th 
                    className={`sortable-header ${getSortDirection('state') ? 'sorted' : ''}`}
                    onClick={() => handleSort('state')}
                  >
                    <div className="th-content">
                      <span>State</span>
                      {getSortDirection('state') && (
                        <span className="sort-indicator">{getSortDirection('state') === 'asc' ? '↑' : '↓'}</span>
                      )}
                    </div>
                  </th>
                  {/* Jul 2026 (JUR-DIR-COUNTY-001) — County column so requesters
                      can distinguish same-name jurisdictions across counties
                      before submitting a duplicate request. */}
                  <th 
                    className={`sortable-header ${getSortDirection('display_county') ? 'sorted' : ''}`}
                    onClick={() => handleSort('display_county')}
                    data-testid="jurisdiction-county-header"
                  >
                    <div className="th-content">
                      <span>County</span>
                      {getSortDirection('display_county') && (
                        <span className="sort-indicator">{getSortDirection('display_county') === 'asc' ? '↑' : '↓'}</span>
                      )}
                    </div>
                  </th>
                  <th 
                    className={`sortable-header ${getSortDirection('jurisdiction_name') ? 'sorted' : ''}`}
                    onClick={() => handleSort('jurisdiction_name')}
                  >
                    <div className="th-content">
                      <span>Jurisdiction Name</span>
                      {getSortDirection('jurisdiction_name') && (
                        <span className="sort-indicator">{getSortDirection('jurisdiction_name') === 'asc' ? '↑' : '↓'}</span>
                      )}
                    </div>
                  </th>
                  <th 
                    className={`sortable-header ${getSortDirection('display_check_payable') ? 'sorted' : ''}`}
                    onClick={() => handleSort('display_check_payable')}
                    data-testid="jurisdiction-check-payable-header"
                  >
                    <div className="th-content">
                      <span>Check Payable Name</span>
                      {getSortDirection('display_check_payable') && (
                        <span className="sort-indicator">{getSortDirection('display_check_payable') === 'asc' ? '↑' : '↓'}</span>
                      )}
                    </div>
                  </th>
                  <th>Phone</th>
                  <th>Physical Address</th>
                  <th 
                    className={`sortable-header ${getSortDirection('created_at') ? 'sorted' : ''}`}
                    onClick={() => handleSort('created_at')}
                  >
                    <div className="th-content">
                      <span>Created</span>
                      {getSortDirection('created_at') && (
                        <span className="sort-indicator">{getSortDirection('created_at') === 'asc' ? '↑' : '↓'}</span>
                      )}
                    </div>
                  </th>
                  {/* Last Updated column — surfaced by the Activity Timeline feature
                      (Feb 22, 2026 — Session 19). Backend returns `updated_at` on
                      the list response; `updated_by_name` is hydrated here via the
                      audit endpoint when the drawer is opened. Table shows just the
                      timestamp to stay compact. */}
                  <th
                    className={`sortable-header ${getSortDirection('updated_at') ? 'sorted' : ''}`}
                    onClick={() => handleSort('updated_at')}
                  >
                    <div className="th-content">
                      <span>Last Updated</span>
                      {getSortDirection('updated_at') && (
                        <span className="sort-indicator">{getSortDirection('updated_at') === 'asc' ? '↑' : '↓'}</span>
                      )}
                    </div>
                  </th>
                  <th className="actions-col" data-testid="jurisdiction-actions-header">Actions</th>
                </tr>
              </thead>
              <tbody>
                {sortedJurisdictions.map((jurisdiction) => (
                  <tr key={jurisdiction.id || Math.random()} className={!jurisdiction.is_active ? 'inactive-row' : ''}>
                    <td>
                      <span className={`status-badge ${jurisdiction.is_active ? 'status-active' : 'status-inactive'}`}>
                        {jurisdiction.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td>
                      <span className={`type-badge ${getTypeBadgeColor(jurisdiction.jurisdiction_type)}`}>
                        {formatType(jurisdiction.jurisdiction_type, jurisdiction.state)}
                      </span>
                    </td>
                    <td className="state-cell">
                      <span className="state-badge">{jurisdiction.state || ''}</span>
                    </td>
                    <td className="county-cell" data-testid={`jurisdiction-county-${jurisdiction.id}`}>
                      {jurisdiction.display_county || '—'}
                    </td>
                    <td className="jurisdiction-name">
                      {jurisdiction.jurisdiction_name || '—'}
                    </td>
                    <td className="check-payable-cell" data-testid={`jurisdiction-check-payable-${jurisdiction.id}`}>
                      {jurisdiction.display_check_payable || '—'}
                      {/* JURDIR-PAYEE-002: fallback shown as such — the dedicated payee field is empty on this record */}
                      {jurisdiction.display_check_payable && !(jurisdiction.make_checks_payable_to || '').trim() && (
                        <span className="ml-1 inline-block rounded px-1 text-[10px] font-semibold uppercase tracking-wide bg-amber-50 text-amber-700" title="No dedicated payee stored — showing the Tax Collector Name fallback" data-testid={`jurisdiction-payee-fallback-badge-${jurisdiction.id}`}>fallback</span>
                      )}
                    </td>
                    <td className="phone-cell">{formatUSPhone(pickPayeePhone(jurisdiction))}</td>
                    <td className="address-cell">
                      <span
                        className="cell-truncate"
                        title={jurisdiction.tax_collector_physical_address || pickPayeeMailingAddress(jurisdiction) || ''}
                      >
                        {jurisdiction.tax_collector_physical_address || pickPayeeMailingAddress(jurisdiction) || '—'}
                      </span>
                    </td>
                    <td className="date-cell">{formatDate(jurisdiction.created_at)}</td>
                    <td className="date-cell">
                      {jurisdiction.updated_at ? formatDate(jurisdiction.updated_at) : '—'}
                    </td>
                    <td className="actions-col">
                      <div className="action-buttons" data-testid={`jurisdiction-actions-${jurisdiction.id}`}>
                        {currentUser?.role !== 'jurisdiction_requestor' && (
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => handleEdit(jurisdiction)}
                            disabled={actionLoading === jurisdiction.id}
                            title="Edit"
                            data-testid={`edit-${jurisdiction.id}`}
                          >
                            <Pencil className="w-4 h-4" />
                          </Button>
                        )}
                        {(currentUser?.role === 'admin' || currentUser?.role === 'jurisdiction_manager') && (
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => setAuditDrawer({ isOpen: true, jurisdiction })}
                            title="View Activity"
                            data-testid={`view-activity-${jurisdiction.id}`}
                          >
                            <History className="w-4 h-4" />
                          </Button>
                        )}
                        {/* Feb 27, 2026 (revised) — Admin-only HARD delete.
                            Active/Inactive toggle lives on the Edit dialog
                            and is intentionally separate from this action. */}
                        {currentUser?.role === 'admin' && (
                          <Button
                            variant="ghost"
                            size="icon"
                            onClick={() => openDeleteConfirm(jurisdiction)}
                            disabled={actionLoading === jurisdiction.id}
                            title="Permanently delete"
                            data-testid={`delete-${jurisdiction.id}`}
                            className="text-rose-600 hover:bg-rose-50"
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination — First / Prev / page numbers / Next / Last + jump-to-page */}
          {pagination.totalPages > 1 && (
            <nav className="pagination" role="navigation" aria-label="Pagination" data-testid="jurisdiction-directory-pagination">
              <div className="pagination-controls">
                <Button
                  variant="outline"
                  size="sm"
                  className="pagination-btn pagination-btn-icon"
                  onClick={() => handlePageChange(1)}
                  disabled={pagination.page === 1}
                  aria-label="Go to first page"
                  data-testid="pagination-first-btn"
                  title="First page"
                >
                  <ChevronsLeft className="w-4 h-4" />
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  className="pagination-btn pagination-btn-icon"
                  onClick={() => handlePageChange(pagination.page - 1)}
                  disabled={pagination.page === 1}
                  aria-label="Go to previous page"
                  data-testid="pagination-prev-btn"
                  title="Previous page"
                >
                  <ChevronLeft className="w-4 h-4" />
                </Button>

                <ul className="pagination-numbers" role="list">
                  {buildPageList(pagination.page, pagination.totalPages).map((p) => {
                    if (typeof p === 'string') {
                      return (
                        <li key={p} className="pagination-ellipsis" aria-hidden="true">…</li>
                      );
                    }
                    const isActive = p === pagination.page;
                    return (
                      <li key={p}>
                        <button
                          type="button"
                          className={`pagination-number${isActive ? ' pagination-number-active' : ''}`}
                          onClick={() => handlePageChange(p)}
                          aria-current={isActive ? 'page' : undefined}
                          aria-label={isActive ? `Page ${p}, current page` : `Go to page ${p}`}
                          data-testid={`pagination-page-${p}-btn`}
                        >
                          {p}
                        </button>
                      </li>
                    );
                  })}
                </ul>

                <Button
                  variant="outline"
                  size="sm"
                  className="pagination-btn pagination-btn-icon"
                  onClick={() => handlePageChange(pagination.page + 1)}
                  disabled={pagination.page >= pagination.totalPages}
                  aria-label="Go to next page"
                  data-testid="pagination-next-btn"
                  title="Next page"
                >
                  <ChevronRight className="w-4 h-4" />
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  className="pagination-btn pagination-btn-icon"
                  onClick={() => handlePageChange(pagination.totalPages)}
                  disabled={pagination.page >= pagination.totalPages}
                  aria-label="Go to last page"
                  data-testid="pagination-last-btn"
                  title="Last page"
                >
                  <ChevronsRight className="w-4 h-4" />
                </Button>
              </div>

              <div className="pagination-meta">
                <span className="pagination-info" data-testid="pagination-info">
                  Page <strong>{pagination.page}</strong> of <strong>{pagination.totalPages}</strong>
                  <span className="pagination-total"> · {pagination.total.toLocaleString()} total</span>
                </span>
                <form
                  className="pagination-jump"
                  onSubmit={(e) => { e.preventDefault(); commitPageJump(); }}
                  aria-label="Jump to page"
                >
                  <label htmlFor="pagination-jump-input" className="pagination-jump-label">Go to</label>
                  <input
                    id="pagination-jump-input"
                    type="number"
                    min="1"
                    max={pagination.totalPages}
                    value={pageJumpInput}
                    onChange={(e) => setPageJumpInput(e.target.value)}
                    onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); commitPageJump(); } }}
                    placeholder={String(pagination.page)}
                    className="pagination-jump-input"
                    data-testid="pagination-jump-input"
                    aria-label="Page number"
                  />
                  <Button
                    type="submit"
                    variant="outline"
                    size="sm"
                    className="pagination-jump-go"
                    disabled={!pageJumpInput}
                    data-testid="pagination-jump-go-btn"
                  >
                    Go
                  </Button>
                </form>
              </div>
            </nav>
          )}
        </>
      )}

      {/* Jurisdiction Wizard Modal (Stepper Form) */}
      <JurisdictionWizardModal
        isOpen={formDialogOpen}
        jurisdiction={editingJurisdiction}
        mode={currentUser?.role === 'jurisdiction_requestor' ? 'request' : 'auto'}
        defaultTab={pendingFocusTab || undefined}
        onClose={() => {
          setFormDialogOpen(false);
          setEditingJurisdiction(null);
          setPendingFocusTab(null);
        }}
        onSave={async () => {
          setFormDialogOpen(false);
          setEditingJurisdiction(null);
          setPendingFocusTab(null);
          // For requestors, route them to their queue after submission;
          // everyone else refreshes the directory list in-place.
          if (currentUser?.role === 'jurisdiction_requestor') {
            navigate('/admin/jurisdiction-requests');
          } else {
            await fetchJurisdictions();
          }
        }}
        onViewActivity={(jurisdiction) => {
          setAuditDrawer({ isOpen: true, jurisdiction });
        }}
      />

      {/* Activity Timeline Drawer (Feb 22, 2026 — Session 19) */}
      <JurisdictionAuditDrawer
        isOpen={auditDrawer.isOpen}
        jurisdictionId={auditDrawer.jurisdiction?.id}
        jurisdictionName={auditDrawer.jurisdiction?.jurisdiction_name}
        onClose={() => setAuditDrawer({ isOpen: false, jurisdiction: null })}
      />

      {/* CSV Upload Modal */}
      {uploadModalOpen && (
        <CSVUploadModal
          onClose={() => setUploadModalOpen(false)}
          onSuccess={() => {
            setUploadModalOpen(false);
            fetchJurisdictions();
          }}
          token={token}
        />
      )}

      {/* Feb 27, 2026 (revised) — Admin HARD-delete confirmation modal with
          dependency safeguard. Two states:
            (a) has_dependencies → blocked, Cancel only
            (b) safe → permanent-delete warning + Confirm */}
      {deleteModal.isOpen && deleteModal.target && (
        <div
          className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4"
          data-testid="delete-jurisdiction-modal"
          onClick={(e) => {
            if (e.target === e.currentTarget && !deleteModal.submitting) {
              setDeleteModal({ isOpen: false, target: null, deps: null, depsLoading: false, submitting: false });
            }
          }}
        >
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full overflow-hidden">
            <div className="px-5 py-4 bg-rose-50 border-b border-rose-100 flex items-start gap-3">
              <AlertTriangle className="w-6 h-6 text-rose-600 shrink-0 mt-0.5" />
              <div>
                <h3 className="text-base font-semibold text-rose-900">
                  {deleteModal.deps?.has_dependencies
                    ? 'Cannot permanently delete'
                    : 'Permanently delete this jurisdiction?'}
                </h3>
                <p className="text-sm text-rose-700 mt-0.5">
                  {deleteModal.target.jurisdiction_name} ({deleteModal.target.state})
                </p>
              </div>
            </div>
            <div className="px-5 py-4 space-y-3 text-sm text-slate-700">
              {deleteModal.depsLoading ? (
                <p className="text-slate-500 italic" data-testid="delete-modal-loading">
                  Checking dependencies…
                </p>
              ) : deleteModal.deps?.has_dependencies ? (
                <div
                  className="bg-amber-50 border border-amber-200 rounded-lg p-3"
                  data-testid="delete-modal-blocked"
                >
                  <p className="font-semibold text-amber-900 mb-1">
                    This jurisdiction is already referenced in {deleteModal.deps.dependency_counts.total} record(s):
                  </p>
                  <ul className="text-xs text-amber-800 space-y-0.5 ml-4 list-disc">
                    {deleteModal.deps.dependency_counts.orders > 0 && (
                      <li>{deleteModal.deps.dependency_counts.orders} order(s)</li>
                    )}
                    {deleteModal.deps.dependency_counts.tax_certificates > 0 && (
                      <li>{deleteModal.deps.dependency_counts.tax_certificates} tax certificate(s)</li>
                    )}
                    {deleteModal.deps.dependency_counts.invoices > 0 && (
                      <li>{deleteModal.deps.dependency_counts.invoices} invoice(s)</li>
                    )}
                  </ul>
                  <p className="text-xs text-amber-900 mt-2">
                    It cannot be permanently deleted. Please mark it{' '}
                    <strong>Inactive</strong> from the Edit dialog instead — this
                    preserves all historical records.
                  </p>
                </div>
              ) : (
                <div data-testid="delete-modal-safe">
                  <p className="text-slate-700 mb-2">
                    No references found. This jurisdiction will be{' '}
                    <strong>permanently removed</strong> from the database.
                  </p>
                  <ul className="text-xs text-rose-700 space-y-0.5 ml-4 list-disc">
                    <li>This action is <strong>irreversible</strong>.</li>
                    <li>The row will disappear from Active, Inactive, and All filters.</li>
                    <li>It will no longer appear in dropdowns, duplicate detection, search, or exports.</li>
                  </ul>
                </div>
              )}
            </div>
            <div className="px-5 py-3 bg-slate-50 border-t border-slate-200 flex justify-end gap-2">
              <Button
                variant="outline"
                onClick={() => setDeleteModal({ isOpen: false, target: null, deps: null, depsLoading: false, submitting: false })}
                disabled={deleteModal.submitting}
                data-testid="delete-cancel-btn"
              >
                {deleteModal.deps?.has_dependencies ? 'Close' : 'Cancel'}
              </Button>
              {!deleteModal.depsLoading && !deleteModal.deps?.has_dependencies && (
                <Button
                  onClick={handleConfirmDelete}
                  disabled={deleteModal.submitting}
                  className="bg-rose-600 hover:bg-rose-700 text-white"
                  data-testid="delete-confirm-btn"
                >
                  <Trash2 className="w-4 h-4 mr-1" />
                  {deleteModal.submitting ? 'Deleting…' : 'Permanently Delete'}
                </Button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Confirm Dialog */}
      <ConfirmDialog
        isOpen={confirmDialog.isOpen}
        title={confirmDialog.title}
        message={confirmDialog.message}
        onConfirm={confirmDialog.onConfirm}
        onClose={() => setConfirmDialog({ isOpen: false })}
      />
    </div>
  );
};

// Jurisdiction Form Dialog Component
const JurisdictionFormDialog = ({ jurisdiction, onClose, onSuccess, token }) => {
  const [formData, setFormData] = useState({
    jurisdiction_type: jurisdiction?.jurisdiction_type || 'county',
    state: jurisdiction?.state || '',
    jurisdiction_name: jurisdiction?.jurisdiction_name || '',
    tax_collector_name: jurisdiction?.tax_collector_name || '',
    tax_collector_phone: jurisdiction?.tax_collector_phone || '',
    tax_collector_phone_ext: jurisdiction?.tax_collector_phone_ext || '',
    tax_collector_mailing_address: jurisdiction?.tax_collector_mailing_address || '',
    tax_collector_physical_address: jurisdiction?.tax_collector_physical_address || '',
    tax_collector_working_hours: jurisdiction?.tax_collector_working_hours || '',
    tax_collector_email: jurisdiction?.tax_collector_email || '',
    tax_collector_website: jurisdiction?.tax_collector_website || '',
    assessor_website: jurisdiction?.assessor_website || '',
    tax_collector_fax: jurisdiction?.tax_collector_fax || '',
    notes: jurisdiction?.notes || '',
    mlc_charges: jurisdiction?.mlc_charges || '',
    mlc_24_month_charges: jurisdiction?.mlc_24_month_charges || '',
    mlc_eta_days: jurisdiction?.mlc_eta_days || '',
    sase_required: jurisdiction?.sase_required ?? null,
    info_obtained_back: jurisdiction?.info_obtained_back || '',
    is_active: jurisdiction?.is_active !== undefined ? jurisdiction.is_active : true,
    // Parent County fields for sub-jurisdictions
    parent_county_id: jurisdiction?.parent_county_id || '',
    parent_county_name: jurisdiction?.parent_county_name || ''
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [showDeactivateConfirm, setShowDeactivateConfirm] = useState(false);
  
  // Parent county selection for sub-jurisdictions
  const [availableCounties, setAvailableCounties] = useState([]);
  const [loadingCounties, setLoadingCounties] = useState(false);
  
  // Sub-jurisdiction types that require parent county
  const SUB_JURISDICTION_TYPES = ['city', 'town', 'township', 'village', 'school_district', 'municipality'];
  const PARENT_EXEMPT_STATES = ['NJ', 'RI', 'MA', 'NH', 'ME', 'VT', 'CT'];
  const isSubJurisdictionType = SUB_JURISDICTION_TYPES.includes(formData.jurisdiction_type);
  const isParentExemptState = PARENT_EXEMPT_STATES.includes((formData.state || '').toUpperCase());
  const requiresParent = isSubJurisdictionType && !isParentExemptState;
  
  // Fetch available counties when state changes or when type changes to sub-jurisdiction
  useEffect(() => {
    const fetchCounties = async () => {
      if (!requiresParent || !formData.state) {
        setAvailableCounties([]);
        return;
      }
      
      setLoadingCounties(true);
      try {
        const params = new URLSearchParams({
          jurisdiction_type: 'county',
          state: formData.state.toUpperCase(),
          active_only: 'true',
          page_size: '500'
        });
        
        const response = await fetch(`${API_BASE_URL}/api/jurisdictions?${params.toString()}`, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });
        
        if (response.ok) {
          const data = await response.json();
          setAvailableCounties(data.jurisdictions || []);
        }
      } catch (err) {
        console.error('Error fetching counties:', err);
      } finally {
        setLoadingCounties(false);
      }
    };
    
    fetchCounties();
  }, [formData.state, formData.jurisdiction_type, isSubJurisdictionType, token]);
  
  // Handle parent county selection
  const handleParentCountyChange = (e) => {
    const countyId = e.target.value;
    const selectedCounty = availableCounties.find(c => c.id === countyId);
    
    setFormData(prev => ({
      ...prev,
      parent_county_id: countyId || '',
      parent_county_name: selectedCounty?.jurisdiction_name || ''
    }));
  };

  // Phone number formatter: XXX-XXX-XXXX
  const formatPhoneNumber = (value) => {
    // Remove all non-numeric characters
    const phoneNumber = stripNonNumeric(value);
    
    // Format as user types (progressive formatting)
    if (phoneNumber.length === 0) {
      return '';
    } else if (phoneNumber.length <= 3) {
      return `(${phoneNumber}`;
    } else if (phoneNumber.length <= 6) {
      return `(${phoneNumber.slice(0, 3)}) ${phoneNumber.slice(3)}`;
    } else {
      return `(${phoneNumber.slice(0, 3)}) ${phoneNumber.slice(3, 6)}-${phoneNumber.slice(6, 10)}`;
    }
  };

  // Handle phone blur to validate and format
  const handlePhoneBlur = (e) => {
    const { name, value } = e.target;
    
    if (name === 'tax_collector_phone' && value) {
      const result = validateAndFormatPhone(value);
      
      if (!result.valid && result.error) {
        // Show validation error
        toast.error(result.error);
        // Keep the invalid value for user to fix
      } else if (result.valid) {
        // Format to final format
        setFormData(prev => ({ ...prev, [name]: result.formatted }));
      }
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    
    // Fields that should be converted to uppercase
    const uppercaseFields = [
      'state',
      'jurisdiction_name',
      'tax_collector_name',
      'tax_collector_mailing_address',
      'tax_collector_physical_address',
      'tax_collector_working_hours',
      'notes'
    ];
    
    // Phone number formatting
    let finalValue = value;
    if (name === 'tax_collector_phone') {
      finalValue = formatPhoneNumber(value);
    } else if (name === 'tax_collector_phone_ext') {
      // Extension: only allow digits, max 6
      finalValue = value.replace(/\D/g, '').slice(0, 6);
    } else if (uppercaseFields.includes(name)) {
      // Convert to uppercase for alphabetic text fields
      finalValue = value.toUpperCase();
    }
    
    setFormData(prev => ({ ...prev, [name]: finalValue }));
  };

  // Helper function to normalize URLs (auto-prepend https:// if missing)
  const normalizeURL = (url) => {
    if (!url || url.trim() === '') return null;
    
    const trimmedURL = url.trim();
    
    // If URL already has protocol, return as-is
    if (trimmedURL.startsWith('http://') || trimmedURL.startsWith('https://')) {
      return trimmedURL;
    }
    
    // Auto-prepend https://
    return `https://${trimmedURL}`;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError(null);

    // Validate parent county for sub-jurisdiction types (skip for exempt states)
    if (requiresParent && !formData.parent_county_id) {
      setError('Parent County is required for this jurisdiction type. Please select a county.');
      toast.error('Parent County is required for this jurisdiction type.');
      setSaving(false);
      return;
    }

    try {
      const url = jurisdiction 
        ? `${API_BASE_URL}/api/jurisdictions/${jurisdiction.id}`
        : `${API_BASE_URL}/api/jurisdictions`;
      
      const method = jurisdiction ? 'PUT' : 'POST';

      // Prepare form data with proper null handling for numeric fields
      const submitData = { ...formData };
      
      // Convert empty strings to null for numeric fields
      if (submitData.mlc_charges === '') {
        submitData.mlc_charges = null;
      }
      if (submitData.mlc_24_month_charges === '') {
        submitData.mlc_24_month_charges = null;
      }
      if (submitData.mlc_eta_days === '') {
        submitData.mlc_eta_days = null;
      }
      
      // Handle parent county fields
      if (submitData.parent_county_id === '') {
        submitData.parent_county_id = null;
      }
      if (submitData.parent_county_name === '') {
        submitData.parent_county_name = null;
      }
      
      // Clear parent county for county type (counties don't have parents)
      if (submitData.jurisdiction_type === 'county') {
        submitData.parent_county_id = null;
        submitData.parent_county_name = null;
      }
      
      // Normalize URL fields (auto-prepend https:// if missing)
      submitData.tax_collector_website = normalizeURL(submitData.tax_collector_website);
      submitData.assessor_website = normalizeURL(submitData.assessor_website);
      
      // Convert empty strings to null for optional text fields
      if (submitData.tax_collector_email === '') {
        submitData.tax_collector_email = null;
      }
      if (submitData.tax_collector_working_hours === '') {
        submitData.tax_collector_working_hours = null;
      }
      if (submitData.tax_collector_fax === '') {
        submitData.tax_collector_fax = null;
      }
      if (submitData.notes === '') {
        submitData.notes = null;
      }
      if (submitData.tax_collector_physical_address === '') {
        submitData.tax_collector_physical_address = null;
      }
      if (submitData.info_obtained_back === '') {
        submitData.info_obtained_back = null;
      }

      // Log what we're sending
      console.log('Submitting jurisdiction:', {
        method,
        url,
        formData: submitData
      });

      const response = await fetch(url, {
        method,
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(submitData)
      });

      if (!response.ok) {
        let errorMessage = 'Failed to save jurisdiction';
        try {
          const errorData = await response.json();
          // Handle different error response formats
          if (typeof errorData.detail === 'string') {
            errorMessage = errorData.detail;
          } else if (typeof errorData.detail === 'object') {
            errorMessage = JSON.stringify(errorData.detail);
          } else if (errorData.message) {
            errorMessage = errorData.message;
          }
        } catch (parseError) {
          console.error('Error parsing error response:', parseError);
          errorMessage = `Server error: ${response.status} ${response.statusText}`;
        }
        throw new Error(errorMessage);
      }

      const responseData = await response.json();
      console.log('Jurisdiction saved successfully:', responseData);
      console.log('Updated is_active value:', responseData.is_active);

      toast.success(jurisdiction ? 'Jurisdiction updated successfully' : 'Jurisdiction created successfully');
      
      // Small delay to ensure backend has persisted the data
      await new Promise(resolve => setTimeout(resolve, 500));
      
      onSuccess();
    } catch (err) {
      console.error('Error saving jurisdiction:', err);
      const errorMsg = err.message || 'An unknown error occurred';
      setError(errorMsg);
      toast.error(errorMsg);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="modal-overlay">
      {/* Backdrop click disabled to prevent accidental data loss */}
      <div className="modal-content jurisdiction-form-modal" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2>{jurisdiction ? 'Edit Jurisdiction' : 'Add New Jurisdiction'}</h2>
          <Button variant="ghost" size="icon" onClick={onClose}>
            <X className="w-4 h-4" />
          </Button>
        </div>

        <form onSubmit={handleSubmit} className="jurisdiction-form">
          <div className="form-grid">
            {/* Type and State - Same Row */}
            <div className="form-group">
              <label htmlFor="jurisdiction_type">Type *</label>
              <select
                id="jurisdiction_type"
                name="jurisdiction_type"
                value={formData.jurisdiction_type}
                onChange={handleChange}
                required
                className="form-control"
              >
                {/* Feb 2026 — canonical 9-item taxonomy; drops the
                    legacy "Other" option (per AC #4). Existing records
                    saved with a legacy value still render below as
                    disabled fallback so they remain visible. */}
                {JURISDICTION_TYPE_OPTIONS.map((opt) => (
                  <option key={opt.value} value={opt.value}>{opt.label}</option>
                ))}
                {formData.jurisdiction_type &&
                  !isCanonicalJurisdictionType(formData.jurisdiction_type) && (
                    <option value={formData.jurisdiction_type} disabled>
                      {getJurisdictionTypeLabel(formData.jurisdiction_type)}
                    </option>
                  )}
              </select>
            </div>

            <div className="form-group">
              <label htmlFor="state">State *</label>
              <input
                type="text"
                id="state"
                name="state"
                value={formData.state}
                onChange={handleChange}
                required
                placeholder="PA"
                maxLength="2"
                className="form-control uppercase-field"
                pattern="[A-Z]{2}"
                title="Enter 2-letter state code (e.g., PA, DE, FL)"
                style={{ textTransform: 'uppercase' }}
              />
            </div>

            {/* Parent County - Required for sub-jurisdiction types */}
            {isSubJurisdictionType && (
              <div className="form-group full-width">
                <label htmlFor="parent_county_id">
                  Parent County *
                  <span className="field-hint"> (Required for {formData.jurisdiction_type})</span>
                </label>
                <select
                  id="parent_county_id"
                  name="parent_county_id"
                  value={formData.parent_county_id}
                  onChange={handleParentCountyChange}
                  required
                  className="form-control"
                  disabled={loadingCounties || !formData.state}
                >
                  <option value="">
                    {loadingCounties ? 'Loading counties...' : 
                     !formData.state ? '-- Enter state first --' :
                     availableCounties.length === 0 ? '-- No counties in this state --' :
                     '-- Select Parent County --'}
                  </option>
                  {availableCounties.map((county) => (
                    <option key={county.id} value={county.id}>
                      {county.jurisdiction_name}
                    </option>
                  ))}
                </select>
                {formData.state && availableCounties.length === 0 && !loadingCounties && (
                  <p className="field-warning" style={{ color: '#d97706', fontSize: '0.75rem', marginTop: '4px' }}>
                    No counties found for {formData.state}. Please add a County jurisdiction first.
                  </p>
                )}
              </div>
            )}

            {/* Jurisdiction Name */}
            <div className="form-group full-width">
              <label htmlFor="jurisdiction_name">Jurisdiction Name *</label>
              <input
                type="text"
                id="jurisdiction_name"
                name="jurisdiction_name"
                value={formData.jurisdiction_name}
                onChange={handleChange}
                required
                placeholder="e.g., MIAMI-DADE COUNTY"
                className="form-control uppercase-field"
              />
            </div>

            {/* Check Payable Name */}
            <div className="form-group full-width">
              <label htmlFor="tax_collector_name">Check Payable Name *</label>
              <input
                type="text"
                id="tax_collector_name"
                name="tax_collector_name"
                value={formData.tax_collector_name}
                onChange={handleChange}
                required
                placeholder="e.g., WAYNE COUNTY TREASURER"
                className="form-control uppercase-field"
              />
            </div>

            {/* Phone and Email Row - Phone takes more space */}
            <div className="form-group phone-group">
              <label htmlFor="tax_collector_phone">Phone *</label>
              <div className="phone-ext-row">
                <input
                  type="tel"
                  id="tax_collector_phone"
                  name="tax_collector_phone"
                  value={formData.tax_collector_phone}
                  onChange={handleChange}
                  onBlur={handlePhoneBlur}
                  required
                  placeholder="(302) 395-5340"
                  maxLength="14"
                  className="form-control phone-input"
                  title="Enter 10-digit US phone number"
                />
                <input
                  type="text"
                  id="tax_collector_phone_ext"
                  name="tax_collector_phone_ext"
                  value={formData.tax_collector_phone_ext}
                  onChange={handleChange}
                  placeholder="Ext"
                  maxLength="6"
                  className="form-control ext-input"
                  title="Extension (optional, digits only)"
                />
              </div>
            </div>

            {/* Email */}
            <div className="form-group email-group">
              <label htmlFor="tax_collector_email">Email</label>
              <input
                type="email"
                id="tax_collector_email"
                name="tax_collector_email"
                value={formData.tax_collector_email}
                onChange={handleChange}
                placeholder="taxes@county.gov"
                className="form-control"
              />
            </div>

            {/* Mailing Address (Optional) */}
            <div className="form-group full-width">
              <label htmlFor="tax_collector_mailing_address">Mailing Address (if different from Physical)</label>
              <textarea
                id="tax_collector_mailing_address"
                name="tax_collector_mailing_address"
                value={formData.tax_collector_mailing_address}
                onChange={handleChange}
                placeholder="123 MAIN ST, SUITE 100, CITY, STATE 12345"
                className="form-control uppercase-field"
                rows="2"
              />
            </div>

            {/* Physical Address */}
            <div className="form-group full-width">
              <label htmlFor="tax_collector_physical_address">Physical Address *</label>
              <textarea
                id="tax_collector_physical_address"
                name="tax_collector_physical_address"
                value={formData.tax_collector_physical_address}
                onChange={handleChange}
                required
                placeholder="PHYSICAL OFFICE LOCATION"
                className="form-control uppercase-field"
                rows="2"
              />
            </div>

            {/* Working Hours */}
            <div className="form-group">
              <label htmlFor="tax_collector_working_hours">Working Hours</label>
              <input
                type="text"
                id="tax_collector_working_hours"
                name="tax_collector_working_hours"
                value={formData.tax_collector_working_hours}
                onChange={handleChange}
                placeholder="MON-FRI 9AM-5PM"
                className="form-control uppercase-field"
              />
            </div>

            {/* Tax Collector Website */}
            <div className="form-group">
              <label htmlFor="tax_collector_website">Tax Collector Website</label>
              <input
                type="text"
                id="tax_collector_website"
                name="tax_collector_website"
                value={formData.tax_collector_website}
                onChange={handleChange}
                placeholder="www.county.gov/taxcollector (https:// added automatically)"
                className="form-control"
              />
            </div>

            {/* Assessor Website */}
            <div className="form-group">
              <label htmlFor="assessor_website">Assessor Website *</label>
              <input
                type="text"
                id="assessor_website"
                name="assessor_website"
                value={formData.assessor_website}
                onChange={handleChange}
                placeholder="www.county.gov/assessor (https:// added automatically)"
                className="form-control"
                required
              />
            </div>

            {/* Fax */}
            <div className="form-group">
              <label htmlFor="tax_collector_fax">Fax</label>
              <input
                type="tel"
                id="tax_collector_fax"
                name="tax_collector_fax"
                value={formData.tax_collector_fax}
                onChange={handleChange}
                placeholder="(555) 123-4568"
                className="form-control"
              />
            </div>

            {/* Section Divider - Internal Notes */}
            <div className="form-section-divider full-width">
              <h4>Internal Notes</h4>
            </div>

            {/* Notes */}
            <div className="form-group full-width">
              <label htmlFor="notes">Notes & Special Instructions</label>
              <textarea
                id="notes"
                name="notes"
                value={formData.notes}
                onChange={handleChange}
                placeholder="Any internal notes or special instructions..."
                className="form-control uppercase-field"
                rows="2"
              />
            </div>

            {/* Section Divider - MLC & Workflow */}
            <div className="form-section-divider full-width">
              <h4>MLC & Workflow Information</h4>
            </div>

            {/* MLC Charges and 24-Month MLC Charges - Same Row */}
            <div className="form-group">
              <label htmlFor="mlc_charges">MLC Charges ($)</label>
              <input
                type="number"
                step="0.01"
                id="mlc_charges"
                name="mlc_charges"
                value={formData.mlc_charges}
                onChange={handleChange}
                placeholder="50.00"
                className="form-control"
              />
            </div>

            <div className="form-group">
              <label htmlFor="mlc_24_month_charges">24-Month MLC Charges ($)</label>
              <input
                type="number"
                step="0.01"
                id="mlc_24_month_charges"
                name="mlc_24_month_charges"
                value={formData.mlc_24_month_charges}
                onChange={handleChange}
                placeholder="75.00"
                className="form-control"
              />
            </div>

            {/* MLC ETA and SASE Required - Same Row */}
            <div className="form-group">
              <label htmlFor="mlc_eta_days">MLC ETA (days)</label>
              <input
                type="number"
                id="mlc_eta_days"
                name="mlc_eta_days"
                value={formData.mlc_eta_days}
                onChange={handleChange}
                placeholder="5"
                className="form-control"
              />
            </div>

            <div className="form-group">
              <label htmlFor="sase_required">SASE Required</label>
              <select
                id="sase_required"
                name="sase_required"
                value={formData.sase_required === true ? 'true' : formData.sase_required === false ? 'false' : 'inherit'}
                onChange={(e) => setFormData(prev => ({ ...prev, sase_required: e.target.value === 'inherit' ? null : e.target.value === 'true' }))}
                className="form-control"
                data-testid="directory-sase-required-select"
              >
                <option value="inherit">Inherit from collector default</option>
                <option value="true">Yes (explicit override)</option>
                <option value="false">No (explicit override)</option>
              </select>
            </div>

            {/* Info Obtained Back */}
            <div className="form-group full-width">
              <label htmlFor="info_obtained_back">Info Obtained Back</label>
              <select
                id="info_obtained_back"
                name="info_obtained_back"
                value={formData.info_obtained_back}
                onChange={handleChange}
                className="form-control"
              >
                <option value="">Select method...</option>
                <option value="mail">Mail</option>
                <option value="email">Email</option>
                <option value="fax">Fax</option>
              </select>
            </div>
          </div>

          {/* Status Toggle (Only show for editing) */}
          {jurisdiction && (
            <>
              <div className="form-section-divider full-width">
                <h4>Status</h4>
              </div>

              <div className="form-group full-width">
                <label htmlFor="is_active" className="toggle-label">
                  <div className="toggle-switch">
                    <input
                      type="checkbox"
                      id="is_active"
                      checked={formData.is_active}
                      onChange={(e) => {
                        const newValue = e.target.checked;
                        if (!newValue) {
                          // If deactivating, show confirmation
                          setShowDeactivateConfirm(true);
                        } else {
                          // If reactivating, just update
                          setFormData(prev => ({ ...prev, is_active: true }));
                        }
                      }}
                    />
                    <span className="toggle-slider"></span>
                  </div>
                  <span className={`toggle-status ${formData.is_active ? 'status-active' : 'status-inactive'}`}>
                    {formData.is_active ? 'Active' : 'Inactive'}
                  </span>
                </label>
                <p className="toggle-description">
                  {formData.is_active 
                    ? 'This jurisdiction is currently active and will appear in searches and dropdowns.' 
                    : 'This jurisdiction is inactive and will not appear in searches or dropdowns.'}
                </p>
              </div>
            </>
          )}

          {error && <div className="form-error">{error}</div>}

          <div className="form-actions">
            <Button type="button" variant="outline" onClick={onClose} disabled={saving} data-testid="directory-edit-cancel-btn">
              Cancel
            </Button>
            <Button type="submit" disabled={saving} isLoading={saving} data-testid="directory-edit-submit-btn">
              {saving ? 'Saving...' : jurisdiction ? 'Update Jurisdiction' : 'Create Jurisdiction'}
            </Button>
          </div>
        </form>

        {/* Deactivate Confirmation Dialog */}
        {showDeactivateConfirm && (
          <ConfirmDialog
            isOpen={true}
            title="Deactivate Jurisdiction"
            message={`Are you sure you want to deactivate "${jurisdiction?.jurisdiction_name}"? It will no longer appear in searches or dropdowns, but can be reactivated later.`}
            onConfirm={() => {
              setFormData(prev => ({ ...prev, is_active: false }));
              setShowDeactivateConfirm(false);
            }}
            onClose={() => setShowDeactivateConfirm(false)}
            confirmText="Deactivate"
            cancelText="Cancel"
          />
        )}
      </div>
    </div>
  );
};

export default JurisdictionDirectory;
